import { safeFetch } from '../../yields/helpers'
import { wstethFetcher } from '../../yields/intrinsic/fetchers/lido'
import { createWeethFetcher } from '../../yields/intrinsic/fetchers/etherfi'
import { resolveEntryApr } from '../../yields/intrinsic/resolveYieldApr'
import type { LstRegistryEntry } from './registry'

/**
 * Runs every yield source referenced by an LST registry slice and
 * returns a flat `address → apr%` map.
 *
 * The dedup + parallel-fetch + per-entry key lookup is the shared
 * {@link resolveEntryApr} core. LST adds one protocol-specific twist on
 * top:
 *
 *  **weETH special case** — EtherFi's APR is `wstETH APR + LRT2 bonus`,
 *  so it can't be a plain `yieldKey` lookup. We force `wstethFetcher`
 *  into the run via `extraFetchers`, then compose weETH from the same
 *  wstETH result (`createWeethFetcher`) — no second wstETH fetch.
 *
 * Returns `{ [shareAddressLowercase]: aprPercent }`. Missing keys become
 * `0` downstream — never `undefined`.
 */
export const resolveYieldApr = async (
  entries: LstRegistryEntry[],
): Promise<Record<string, number>> => {
  const hasWeEth = entries.some((e) => e.symbol === 'weETH')

  // wstETH is the composition input for weETH — force it into the run
  // even if no entry references it directly by key.
  const { apr, byFetcher } = await resolveEntryApr(
    entries,
    hasWeEth ? [wstethFetcher] : [],
  )

  if (hasWeEth) {
    const wstethRes = byFetcher.get(wstethFetcher)
    const weeth = await safeFetch(
      'WEETH',
      createWeethFetcher(Promise.resolve(wstethRes)).fetch,
    )
    const v = weeth?.['WEETH']
    if (typeof v === 'number' && Number.isFinite(v)) {
      for (const e of entries) {
        if (e.symbol === 'weETH') apr[e.address.toLowerCase()] = v
      }
    }
  }

  return apr
}
